import { useCallback, useEffect, useRef, useState } from "react";

import {
    InputWithTypeahead,
    TypeaheadOptionOrGroup,
    CreateSuggestion,
} from "@impero-com/common/components/typeahead";
import { useAjaxManager } from "@impero-com/common/utils/ajax";
import { formatUserName } from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import { queryClient } from "@impero-com/common/utils/query_client";
import { Svg } from "@impero-com/ui";
import { Trie } from "@tanishiking/aho-corasick";
import { InputStatus } from "antd/es/_util/statusUtils";
import { SelectProps as AntdSelectProps, LabeledValue } from "antd/es/select";
import { cx } from "class-variance-authority";
import { isEmpty } from "lodash-es";

import { UserOverview } from "api/generated/model";
import { getGetUsersQueryKey } from "api/generated/users/users";
import GroupIconSvg from "img/group-icon.svg";
import UserCreatorForm from "org/user/create";
import { useOrgUserProfileCookie } from "utils/profileCookie";
import {
    UserOrUserGroupOverview,
    mapUserOrUserGroupOverview,
} from "utils/user";

import { useAvailableActions } from "../../utils/hooks/useAvailableActions";

import { useSearchUserGroupQuery } from "./hooks/useSearchUserGroupQuery";
import { useSearchUserQuery } from "./hooks/useSearchUserQuery";
import * as theme from "./theme.css";

/**
 * A specialized version of the UserTypeahead widget, for autocompleting user names. Should be instantiated to be used.
 * Props:
 * - `value` (User, required): the user to display in the input field. May be null.
 * - `placeholder` (string, optional): the input placeholder.
 * - `onSelect` (function, required): callback invoked when the user picked an option. Should accept the selected option as parameter.
 */

interface BaseUserTypeaheadProps<T> {
    id: string;
    value: T | undefined;
    filter?: (user: T) => boolean;
    disabled?: boolean;
    status?: InputStatus;
    formatValue: (user: T) => string | LabeledValue;
    formatOption: (searchQuery: string, user: T) => React.ReactNode;
    placeholder?: string;
    startTypingMessage?: string;
    onSelect: (user: T) => void;
    onBlur?: () => void;
    onSelectUser: (user: UserOverview) => void;
    searchFor: (
        searchString: string,
        callback: (items: Array<TypeaheadOptionOrGroup<T>>) => void
    ) => Promise<void>;
    className?: string;
    // Should be (in) the scrollable container
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
    fieldRef?: React.RefCallback<unknown>;
    allowClear?: boolean;
    isAutoFocused?: boolean;
}
function BaseUserTypeahead<T>({
    id,
    formatValue,
    placeholder,
    value,
    status,
    className,
    onSelect,
    onSelectUser,
    onBlur,
    getPopupContainer,
    startTypingMessage,
    searchFor,
    formatOption,
    allowClear,
    fieldRef,
    disabled = false,
    isAutoFocused = false,
}: BaseUserTypeaheadProps<T>) {
    const [searchQuery, setSearchQuery] = useState("");
    const [showUserCreationForm, setShowUserCreationForm] = useState(false);
    const ajaxManager = useAjaxManager();
    const availableActions = useAvailableActions();
    const orgProfileCookie = useOrgUserProfileCookie();

    const renderDropdown = (
        menu: React.ReactElement,
        selectProps?: AntdSelectProps,
        input?: InputWithTypeahead<T>
    ): React.ReactElement => {
        if (input === undefined) {
            return menu;
        }
        const canCreateUser = availableActions.user.user.create;
        return (
            <>
                {startTypingMessage && selectProps?.options?.length === 0 ? (
                    <div className={theme.startTyping}>
                        {startTypingMessage}
                    </div>
                ) : null}
                {canCreateUser ? (
                    <CreateSuggestion
                        label={t("Users_CreateNewUser")}
                        input={input}
                        onClick={() => {
                            setShowUserCreationForm(true);
                        }}
                    />
                ) : null}
                {menu}
            </>
        );
    };

    const search = (
        value: string,
        callback: (items: Array<TypeaheadOptionOrGroup<T>>) => void
    ): Promise<void> => {
        if (value && value.trim() !== "") {
            setSearchQuery(value);
        }
        return searchFor(value, callback);
    };

    const formatUser = (user: T) => {
        return formatOption(searchQuery, user);
    };

    return (
        <>
            <InputWithTypeahead<T>
                id={id}
                fieldRef={fieldRef}
                placeholder={placeholder}
                className={className}
                value={value !== undefined ? formatValue(value) : undefined}
                formatOption={formatUser}
                onSearch={search}
                onSelect={onSelect}
                onBlur={onBlur}
                dropdownRender={renderDropdown}
                getPopupContainer={getPopupContainer}
                disabled={disabled}
                status={status}
                allowClear={allowClear}
                isAutoFocused={isAutoFocused}
            />
            {showUserCreationForm && (
                <UserCreatorForm
                    onUserCreated={(user) => {
                        setShowUserCreationForm(false);
                        void queryClient.invalidateQueries({
                            queryKey: getGetUsersQueryKey(),
                        });
                        onSelectUser(user);
                    }}
                    onCancel={() => {
                        setShowUserCreationForm(false);
                    }}
                    availableActions={availableActions}
                    ajaxManager={ajaxManager}
                    orgProfileCookie={orgProfileCookie}
                />
            )}
        </>
    );
}

export interface UserTypeaheadProps {
    id: string;
    value?: UserOverview;
    filter?: (user: UserOverview) => boolean;
    placeholder?: string;
    onSelect: (user: UserOverview) => void;
    onBlur?: () => void;
    disabled?: boolean;
    status?: InputStatus;
    fieldRef?: React.RefCallback<unknown>;
    allowClear?: boolean;
    // Should be (in) the scrollable container
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
}
export function UserTypeahead({
    id,
    value,
    filter,
    placeholder,
    onSelect,
    onBlur,
    fieldRef,
    status,
    allowClear,
    getPopupContainer,
    disabled = false,
}: UserTypeaheadProps) {
    const [search, setSearch] = useState("");
    const callbackRef = useRef<UserTypeheadCallback>(undefined);
    const { data: matchedUsers, status: queryStatus } =
        useSearchUserQuery(search);

    useEffect(() => {
        if (queryStatus === "success" && callbackRef.current) {
            const filteredUsers = filter
                ? matchedUsers.filter(filter)
                : matchedUsers;
            const userOptions = filteredUsers.map((user: UserOverview) => {
                return {
                    type: "Option" as const,
                    value: user,
                };
            });

            callbackRef.current(userOptions);
        }
    }, [matchedUsers, queryStatus, filter, search]);

    const searchForUser = useCallback(
        (search: string, callback: UserTypeheadCallback): Promise<void> => {
            const trimmedSearch = search.trim();
            setSearch(trimmedSearch);
            callbackRef.current = callback;
            if (trimmedSearch === "") {
                // Short circuit because useSearchUserQuery is disabled on empty queries
                callback([]);
            }
            return Promise.resolve(); // Useless promise, so no need to try and fake the resolution
        },
        []
    );

    return (
        <BaseUserTypeahead<UserOverview>
            fieldRef={fieldRef}
            id={id}
            value={value}
            filter={filter}
            placeholder={placeholder}
            onSelect={onSelect}
            onBlur={onBlur}
            className={theme.userContainer}
            startTypingMessage={t("Users_StartTyping_User")}
            formatValue={formatUserName}
            formatOption={formatMatchedUser}
            onSelectUser={onSelect}
            searchFor={searchForUser}
            status={status}
            disabled={disabled}
            allowClear={allowClear}
            getPopupContainer={getPopupContainer}
        />
    );
}

function formatMatchedUser(
    searchQuery: string,
    user: UserOverview
): React.ReactNode {
    return formatMatch(searchQuery, formatUserName(user) + " - " + user.email);
}

type UserTypeheadCallback = (
    items: Array<TypeaheadOptionOrGroup<UserOverview>>
) => void;

export function UserOrUserGroupTypeahead({
    id,
    value,
    filter,
    placeholder,
    onSelect,
    className,
    getPopupContainer,
    isAutoFocused,
}: UserOrUserGroupTypeaheadProps) {
    const [search, setSearch] = useState("");
    const callbackRef = useRef<UserOrUserGroupTypeaheadCallback>(undefined);
    const { data: matchedUsers, status: usersStatus } =
        useSearchUserQuery(search);
    const { data: matchedUserGroups, status: userGroupsStatus } =
        useSearchUserGroupQuery(search);

    useEffect(() => {
        if (
            usersStatus === "success" &&
            userGroupsStatus === "success" &&
            callbackRef.current
        ) {
            const joinedGroupsWithItsOptions = [
                matchedUsers,
                matchedUserGroups,
            ].flatMap((searchResult) => {
                const matchedUsersOrGroups: Array<UserOrUserGroupOverview> =
                    searchResult;
                const filteredUsersOrGroups = filter
                    ? matchedUsersOrGroups.filter(filter)
                    : matchedUsersOrGroups;
                const options = filteredUsersOrGroups.map(
                    (userOrGroup: UserOrUserGroupOverview) => {
                        return {
                            type: "Option" as const,
                            value: userOrGroup,
                        };
                    }
                );

                if (isEmpty(options)) {
                    return [] as Array<
                        TypeaheadOptionOrGroup<UserOrUserGroupOverview>
                    >;
                } else {
                    const groupName = mapUserOrUserGroupOverview(
                        options[0].value,
                        (_user) => t("User_Users_SearchGroupOptions"),
                        (_userGroup) => t("User_UserGroups_SearchGroupOptions")
                    );

                    return {
                        type: "Group" as const,
                        value: (
                            <div className={theme.optionHeader}>
                                {groupName}
                            </div>
                        ),
                        options,
                    };
                }
            });
            callbackRef.current(joinedGroupsWithItsOptions);
        }
    }, [
        matchedUsers,
        matchedUserGroups,
        usersStatus,
        userGroupsStatus,
        filter,
        search,
    ]);

    const searchForUserAndGroup = useCallback(
        (
            search: string,
            callback: UserOrUserGroupTypeaheadCallback
        ): Promise<void> => {
            const trimmedSearch = search.trim();
            setSearch(trimmedSearch);
            callbackRef.current = callback;
            if (trimmedSearch === "") {
                // Short circuit because useSearchUserQuery is disabled on empty queries
                callback([]);
            }
            return Promise.resolve(); // Useless promise, so no need to try and fake the resolution
        },
        []
    );

    return (
        <BaseUserTypeahead<UserOrUserGroupOverview>
            id={id}
            value={value}
            filter={filter}
            placeholder={placeholder}
            onSelect={onSelect}
            className={cx(theme.userContainer, className)}
            startTypingMessage={t("Users_StartTyping_UserOrGroup")}
            formatValue={formatMatchedUserOrUserGroup}
            formatOption={formatMatchedOption}
            onSelectUser={onSelect}
            searchFor={searchForUserAndGroup}
            getPopupContainer={getPopupContainer}
            isAutoFocused={isAutoFocused}
        />
    );
}

function formatMatchedUserOrUserGroup(
    userOrUserGroup: UserOrUserGroupOverview
) {
    return mapUserOrUserGroupOverview<string | LabeledValue>(
        userOrUserGroup,
        (user) => formatUserName(user),
        (userGroup) => {
            const { name } = userGroup;
            return {
                key: name,
                value: name,
                label: (
                    <div>
                        {name}{" "}
                        <Svg
                            className={theme.userGroupSvg}
                            src={GroupIconSvg}
                        />
                    </div>
                ),
            };
        }
    );
}

function formatMatchedOption(
    searchQuery: string,
    userOrUserGroup: UserOrUserGroupOverview
) {
    return mapUserOrUserGroupOverview<React.ReactNode>(
        userOrUserGroup,
        (user) =>
            formatMatch(searchQuery, `${formatUserName(user)} - ${user.email}`),
        (userGroup) => formatMatch(searchQuery, userGroup.name)
    );
}

export interface UserOrUserGroupTypeaheadProps {
    id: string;
    value?: UserOrUserGroupOverview;
    filter?: (userOrUserGroup: UserOrUserGroupOverview) => boolean;
    placeholder?: string;
    onSelect: (userOrUserGroup: UserOrUserGroupOverview) => void;
    className?: string;
    // Should be (in) the scrollable container
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
    isAutoFocused?: boolean;
}

type UserOrUserGroupTypeaheadCallback = (
    items: Array<TypeaheadOptionOrGroup<UserOrUserGroupOverview>>
) => void;

// exporting only for testing purpose
export function formatMatch(
    searchQuery: string,
    formattedOption: string
): React.ReactNode {
    const searchQueryFragments = searchQuery.toLowerCase().split(" ");
    const trie = new Trie(searchQueryFragments, { allowOverlaps: false });
    const emits = trie.parseText(formattedOption.toLowerCase());

    const highlightedOutput: React.ReactNode[] = [];
    let index = 0;
    emits.forEach((emit, i) => {
        if (emit.start > index) {
            const node = formattedOption.substring(index, emit.start);
            highlightedOutput.push(node);
        }
        const node = (
            <b key={i}>{formattedOption.substring(emit.start, emit.end + 1)}</b>
        );
        highlightedOutput.push(node);
        index = emit.end + 1;
    });

    if (index < formattedOption.length) {
        const node = formattedOption.substring(index);
        highlightedOutput.push(node);
    }

    return highlightedOutput;
}
